/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import performance from '@atlassian/jira-common-performance/src/performance.tsx';
import type { ExtraNavigationTimings, PageSizeMetrics } from './types.tsx';

interface BifrostPayloadStats {
	preHtml?: number;
	serverToClientEvents?: number;
	featureFlags?: number;
	wrmResources?: number;
	serverPerfTrace?: number;
	preloadAssets?: number;
	preloadTags?: number;
	statsigGateValues?: number;
	earlyFeatureFlags?: number;
	inlineScripts?: number;
	contextualPreload?: number;
	contextualState?: number;
	earlyEntryScript?: number;
	inlineCss?: number;
	langPackPreloads?: number;
	langPackScripts?: number;
	externalScripts?: number;
	metaTags?: number;
	lazyPreloads?: number;
	spaHtml?: number;
}

type BifrostPayloadStatsKey = keyof BifrostPayloadStats;

declare global {
	interface Window {
		bifrostPayloadStats?: BifrostPayloadStats;
	}
}

// Helper function to safely access globalThis.bifrostPayloadStats
const getBifrostPayloadStats = (): BifrostPayloadStats | undefined => {
	// @ts-expect-error - globalThis.bifrostPayloadStats is not in standard TypeScript definitions
	return globalThis.bifrostPayloadStats;
};

export const getPageSizeMetrics = (): PageSizeMetrics => {
	let maxDepth = -1;
	let maxWidth = -1;
	let numElements = 0;

	// As described in the lighthouse DOM audit - https://web.dev/dom-size/

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const calculateDOMsizeAndDepth = (el = document.body, depth = 1) => {
		if (!el) {
			return;
		}

		if (depth > maxDepth) {
			maxDepth = depth;
		}

		if (el.children.length > maxWidth) {
			maxWidth = el.children.length;
		}

		let child = el.firstElementChild;
		while (child) {
			// @ts-expect-error - TS2345 - Argument of type 'Element' is not assignable to parameter of type 'HTMLElement'.
			calculateDOMsizeAndDepth(child, depth + 1);
			// If element has shadow dom, traverse into that tree.
			if (child.shadowRoot) {
				// @ts-expect-error - TS2345 - Argument of type 'ShadowRoot' is not assignable to parameter of type 'HTMLElement'.
				calculateDOMsizeAndDepth(child.shadowRoot, depth + 1);
			}
			child = child.nextElementSibling;

			numElements += 1;
		}
	};

	calculateDOMsizeAndDepth();

	return {
		totalBodyElements: numElements,
		maxNestedLevels: maxDepth,
		maxChildElements: maxWidth,
	};
};

export const getExtraNavigationTimings = (): ExtraNavigationTimings => {
	if (!performance) return {};

	const navigation = performance.getEntriesByType('navigation')[0];

	if (!navigation) return {};

	return {
		// From https://www.w3.org/TR/navigation-timing-2/
		// @ts-expect-error - TS2339 - Property 'decodedBodySize' does not exist on type 'PerformanceEntry'.
		decodedBodySize: navigation.decodedBodySize,
		// @ts-expect-error - TS2339 - Property 'encodedBodySize' does not exist on type 'PerformanceEntry'.
		encodedBodySize: navigation.encodedBodySize,
		// @ts-expect-error - TS2339 - Property 'transferSize' does not exist on type 'PerformanceEntry'.
		transferSize: navigation.transferSize,
	};
};

export const getPopInfo = (): ExtraNavigationTimings => {
	if (!performance) return {};

	const navigation = performance.getEntriesByType('navigation')[0];

	if (!navigation) return {};

	return {
		// From https://www.w3.org/TR/navigation-timing-2/
		atlEdgePop:
			// @ts-expect-error - TS2339 - Property 'serverTiming' does not exist on type 'PerformanceEntry'.
			navigation.serverTiming?.find((entry) => entry?.name === 'atl-edge-pop')?.description ??
			undefined,
		cfPop:
			// @ts-expect-error - TS2339 - Property 'serverTiming' does not exist on type 'PerformanceEntry'.
			navigation.serverTiming?.find((entry) => entry?.name === 'cdn-pop')?.description ?? undefined,
	};
};
const assetsTypes = ['script', 'link'];
export const getPageSizeInfo = ({
	start,
	stop,
}: {
	start: number;
	stop: number;
}): Record<string, number | Record<string, number>> => {
	const pageSize = getExtraNavigationTimings();
	if (
		!pageSize ||
		Object.keys(pageSize).length === 0 ||
		pageSize.decodedBodySize === 0 ||
		start > 0
	) {
		return {};
	}

	const decoded: Record<string, number> = {
		total: pageSize.decodedBodySize,
		pageSize: pageSize.decodedBodySize,
		assets: 0,
		endpoints: 0,
	};

	const encoded: Record<string, number> = {
		total: pageSize.encodedBodySize,
		pageSize: pageSize.encodedBodySize,
		assets: 0,
		endpoints: 0,
	};

	const net: Record<string, number> = {
		total: pageSize.transferSize,
		assets: 0,
		endpoints: 0,
	};

	// oxlint-disable-next-line typescript/consistent-type-assertions
	for (const entry of performance.getEntriesByType('resource') as PerformanceResourceTiming[]) {
		if (entry.startTime > stop) {
			// eslint-disable-next-line no-continue
			continue;
		}

		if (assetsTypes.includes(entry.initiatorType)) {
			decoded.total += entry.decodedBodySize;
			decoded.assets += entry.decodedBodySize;
			encoded.total += entry.encodedBodySize;
			encoded.assets += entry.encodedBodySize;
			net.total += entry.transferSize;
			net.assets += entry.transferSize;
		} else {
			decoded.total += entry.decodedBodySize;
			decoded.endpoints += entry.decodedBodySize;
			encoded.total += entry.encodedBodySize;
			encoded.endpoints += entry.encodedBodySize;
			net.total += entry.transferSize;
			net.endpoints += entry.transferSize;
		}
	}

	const returnValue: Record<string, number | Record<string, number>> = {};
	Object.keys(decoded).forEach((key) => {
		returnValue[`size:decoded:${key}`] = decoded[key];
	});
	Object.keys(encoded).forEach((key) => {
		returnValue[`size:encoded:${key}`] = encoded[key];
	});
	Object.keys(net).forEach((key) => {
		returnValue[`size:net:${key}`] = net[key];
	});

	const bifrostStats = getBifrostPayloadStats();

	if (bifrostStats) {
		const sizeSplit: Record<string, number> = {};
		returnValue['size:split'] = sizeSplit;

		const labelsToCheck: BifrostPayloadStatsKey[] = [
			'preHtml',
			'serverToClientEvents',
			'featureFlags',
			'wrmResources',
			'serverPerfTrace',
			'preloadAssets',
			'preloadTags',
			'statsigGateValues',
			'earlyFeatureFlags',
			'inlineScripts',
			'contextualPreload',
			'contextualState',
			'earlyEntryScript',
			'inlineCss',
			'langPackPreloads',
			'langPackScripts',
			'externalScripts',
			'metaTags',
			'lazyPreloads',
			'spaHtml',
		];

		labelsToCheck.forEach((label) => {
			const value = bifrostStats[label];
			if (typeof value === 'number') {
				sizeSplit[label] = value;
			}
		});
	}

	return returnValue;
};
